import './specimen-featured-card.scss'

import _ from 'lodash'
import { switchMap, first } from 'rxjs/operators'
import { defineComponent, PropType } from 'vue'
import { faCamera, faEdit, faCircleNotch } from '@fortawesome/free-solid-svg-icons'

import { reactiveVueMixin } from "@skinsoft/reactive-vue"
import { BasicSpecimenCard } from '../basic-specimen-card'
import { ActionType, LocalizedString, TitleClickAction } from '@skinsoft/ui'
import { specimenSelectors, conditionReportSelectors } from '@skinsoft/selectors'
import { Media, SpecimenResource, LinkableSpecimenItemResource } from '@skinsoft/types'
import { BASIC_SPECIMEN_CARD_TITLE_CLICKED } from '../../../events'
import { DISPLAY_ALERT_TOPIC } from '@skinsoft/bus'
import { MediaTransferHelper } from '@skinsoft/media-transfer-helper'

interface LoadingButton {
    type: string,
    isLoading: boolean,
}

export const SpecimenFeaturedCard = defineComponent({
    mixins: [reactiveVueMixin],
    components: {
        sLocalizedString: LocalizedString,
        sBasicSpecimenCard: BasicSpecimenCard,
    },
    props: {
        specimen: {
            type: Object as PropType<SpecimenResource>,
            required: true
        },
        clickableTitle: {
            type: Boolean,
            default: false,
        },
        titleClickAction: {
            type: Object as PropType<TitleClickAction>
        },
        displayedButtons: {
            type: Array as PropType<any[]>,
            required: true
        },
    },
    template: require('./specimen-featured-card.html'),
    data() {
        return {
            hasLastReport: false,
            lastReportStatus: '',
            lastReportId: '',
            BASIC_SPECIMEN_CARD_TITLE_CLICKED: BASIC_SPECIMEN_CARD_TITLE_CLICKED,
            loadingButtons: [] as LoadingButton[],
            loadingIcon: faCircleNotch,
        }
    },
    mounted() {
        this.loadingButtons = this.displayedButtons?.map(button => {
            return {
                type: button.type,
                isLoading: false,
            }
        })
    },
    computed: {
        firstIdentifier(): string {
            return specimenSelectors.firstIdentifier(this.specimen) ?? ""
        },        
        title(): string {
            return specimenSelectors.title(this.specimen, this.getAppLocale()) ?? ""
        },
    },
    methods: {
        async createNewReport(fromLast: boolean = false, targetRouteName: string) {
            // TODO : Rework SearchResult or create specific object to hold SpecimenResource + needed CR infos?
            // const newReportId = fromLast
            //     ? await this.duplicateReportAndGetId(this.specimen.lastConditionReportId)
            //     : await this.createReportAndGetId()
            const newReportId = await this.createReportAndGetId()

            this.$router.push({
                name: targetRouteName,
                params: {
                    reportId: newReportId,
                },
            })
        },

        async createReportAndGetId(): Promise<string> {
            let createBody = {}
            // Setting concerned item - temporary, TODO use right field when available
            _.set(createBody, 'conditionReportCS.linkableItem', {
                linkableItem: this.specimen.id,
                linkableItemReferenceType: "Specimen",
            } as LinkableSpecimenItemResource)

            let body = {}
            _.set(body, 'conditionReportCS.comments', [])

            const report = await this.$conditionReportDataStore.create(createBody, body).pipe(first()).toPromise()

            return report.id
        },

        async duplicateReportAndGetId(reportToDuplicateId: string): Promise<string> {
            const newReport = await this.$conditionReportDataStore
                .duplicate(reportToDuplicateId)
                .pipe(first())
                .toPromise()
            return newReport.id
        },

        // checkLastReport() {
        //     this.lastReportId = _.get(this.searchFeatured, 'lastConditionReportId')
        //     if (this.lastReportId) {
        //         this.$conditionReportDataStore.fetchOne(this.lastReportId).subscribe((conditionReport) => {
        //             const isArchived: boolean = _.get(conditionReport, 'managementData.archived', false)
        //             this.lastReportStatus = conditionReportSelectors.workflowState(conditionReport)
        //             this.hasLastReport = !isArchived
        //         })
        //     } else {
        //         this.hasLastReport = false
        //     }
        // },

        onTitleClicked() {
            if (this.titleClickAction) {
                if (this.titleClickAction && this.titleClickAction.action === ActionType.EDIT) {
                    this.$router.push({
                        name: this.titleClickAction.targetRouteName,
                        params: { specimenId: this.specimen.id as string },
                        query: { imageChoice: 'false' }
                    })
                }
            }
        },

        performAction(button: any) {
            switch (button.type) {
                case 'camera':
                    let fileInputElement = this.$refs?.camera?.[0]
                    if (fileInputElement instanceof HTMLElement) {
                        fileInputElement.click()
                    }
                    break
                case 'modifySpecimenRecord':
                    this.$router.push({
                        name: this.titleClickAction?.targetRouteName,
                        params: { specimenId: this.specimen.id as string },
                        query: { imageChoice: 'false' },
                    })
                    break
                case 'createNewReport':
                    this.createNewReport(false, button.targetRouteName)
                    break
                case 'createNewReportFromLast':
                    this.createNewReport(true, button.targetRouteName)
                    break
                case 'consultLastReport':
                    this.$router.push({
                        name: button.route,
                        params: { reportId: this.lastReportId }
                    })
                    break
            }
        },

        onFileUploaded(event) {
            this.toggleButtonLoading('camera')

            const myNewFile = new File(
                [event.target.files[0]],
                "REP-" + Date.now() + "." + event.target.files[0].name.split('.').pop(),
                { type: event.target.files[0].type }
            )
            const reader = new FileReader();
            reader.readAsDataURL(myNewFile);

            reader.addEventListener("load", async () => {
                const media = { url: reader.result, file: myNewFile };
                if (media.url == null) {
                    media.url = '';
                } else if (media.url instanceof ArrayBuffer) {
                    media.url = media.url.toString();
                }

                const { cacheKey, fileName } = await this.$imageTransferHelper.uploadImage(media.file.type, media.file, media.url);
                let mediaIdToSave = cacheKey

                let uploadedMedia: Media | null = null
                if (navigator.onLine) {
                    try {
                        uploadedMedia = await this.$imageTransferHelper.repeatFindingMediaIdFromFileNameWithLimit(fileName, 15, 1000)
                    } catch (err) {

                        const alertPayload = {
                            message: this.$t('alerts.medias.failGetIdFromFileName'),
                            variant: "danger"
                        };
                        this.emitOnBus(DISPLAY_ALERT_TOPIC, alertPayload)
                        this.toggleButtonLoading('camera')
                        return
                    }

                    if (uploadedMedia?.id) {
                        mediaIdToSave = uploadedMedia.id
                        this.$imageTransferHelper.addImageToReporterSubAlbum(uploadedMedia.id).subscribe()
                    }
                }

                this.$specimenDataStore.fetchOne(this.specimen.id).pipe(
                    switchMap(specimen => {
                        if (_.get(specimen, 'associatedMedias.medias') === null) {
                            _.set(specimen, 'associatedMedias.medias', [mediaIdToSave]);
                        } else {
                            _.get(specimen, 'associatedMedias.medias').push(mediaIdToSave);
                        }

                        if (MediaTransferHelper.isLocalMedia(mediaIdToSave)) {
                            return this.$specimenDataStore.updateOfflineMode(specimen.id, specimen)
                        } else {
                            return this.$specimenDataStore.update(specimen.id, specimen)
                        }
                    }),
                ).subscribe(() => {
                    this.performAction({ type: "modifySpecimenRecord" })
                })
            }, false);
        },

        getIcon(iconName: string) {
            if (this.buttonIsLoading(iconName)) {
                return this.loadingIcon
            }

            switch (iconName) {
                case 'camera':
                    return faCamera
                case 'edit':
                    return faEdit
            }
        },

        toggleButtonLoading(iconName: string) {
            const cameraIconIndex = this.loadingButtons.findIndex(btn => btn.type == iconName)
            if (cameraIconIndex > -1) {
                const buttonStatus = this.loadingButtons[cameraIconIndex]

                if (buttonStatus) {
                    this.loadingButtons.splice(cameraIconIndex, 1, {
                        type: buttonStatus.type,
                        isLoading: !buttonStatus.isLoading,
                    })
                }
            }
        },

        buttonIsLoading(iconName: string) {
            const loadingButton = this.loadingButtons.find(btn => btn.type === iconName)
            if (loadingButton?.isLoading) {
                return true
            }
            return false
        },
    }
})